import config from '../../../config';
import { CallStatus, CurrencyEnum, PaymentMethodEnum, PaymentProcessEnum, PaymentStatusEnum } from '../../enums';
import {
  AccountPrefixByTradingPlatformEnum,
  OwnerTradingAccount,
  Payment,
  TradingAccountStatusesEnum,
} from '../../models/crm/types';
import { LinkedAccountRequestData } from '../../models/linked-account/types';
import { isHammerWeb, isMyPortalTrade, isTennMob, isTennWeb } from '../../util/auth/helpers';
import { isOneOf } from '../../util/DataHelpers';
import { setCallStatus } from '../../util/error-handling/StatusByCallHelpers';
import { validate } from '../../util/ValidationSchemes/Validate';
import { ValidationType } from '../../util/ValidationSchemes/ValidationTypes';
import { FXRates } from '../reporting/types';
import { isAdjustment, isCardDeposit, isPendingCardTransfer, isPendingTransaction, isWithdrawal } from '../selectors';

import { DEFAULT_TRANSACTIONS_PAGE_SIZE } from './constants';
import { PaymentState } from './index';
import { PaymentCall, RegisterLinkedAccountPayload, TransactionsState } from './types';

export function setPaymentCallStatus(call: PaymentCall, status: CallStatus, state: PaymentState, errorCode?: number) {
  setCallStatus(call, PaymentCall[call], status, state.statusByCall, errorCode, state.errorsLog);
}

const hasCRMStatus = (transaction: Payment, statuses: PaymentStatusEnum[]) => (
  isOneOf(transaction.payment_status, statuses)
);

export const shouldDisplayTransaction = (transaction: Payment) => (
  hasCRMStatus(transaction, [
    PaymentStatusEnum.Completed,
    PaymentStatusEnum.ForApproval,
    PaymentStatusEnum.Rejected,
    PaymentStatusEnum.Canceled,
  ])
  || isCardDeposit(transaction)
);

export function parsePayments(
  receivedTransactions: Payment[] | null | undefined,
  allTransactions: TransactionsState,
  firstTransactions?: TransactionsState,
  deposits?: Payment[],
  withdrawals?: Payment[],
) {
  const {
    filteredOut,
    ignored,
  } = allTransactions;

  receivedTransactions?.forEach((payment: Payment, index: number) => {
    if (isPendingCardTransfer(payment)) {
      ignored.push(payment);
      return; // Web case - back button from DSK page
    }

    if (shouldDisplayTransaction(payment)) {
      allTransactions.data.push(payment);
      if (firstTransactions && firstTransactions.data.length < DEFAULT_TRANSACTIONS_PAGE_SIZE) {
        firstTransactions.data.push(payment);
      }
    } else {
      filteredOut.push(payment);
    }

    if (deposits && isAdjustment(payment)) deposits.push(payment);
    if (withdrawals && isWithdrawal(payment)) withdrawals.push(payment);
  });
}

export function setCallForRefresh(state: PaymentState, call: PaymentCall) {
  if (!state.callsForRefresh) state.callsForRefresh = {} as any;
  state.callsForRefresh![call] = true;
}

// eslint-disable-next-line max-len
export const getBankStatementRequestBody = (individualId: number | null, bankAccountDetails: RegisterLinkedAccountPayload): LinkedAccountRequestData => {
  const {
    bankName,
    bankCountryId,
    file,
    swiftCode,
    currencyCode,
    iban,
  } = bankAccountDetails;
  const ibanToUpperCase = iban.toUpperCase();

  let ibanOrAccount = {};
  if (validate(iban, ValidationType.IBAN)) {
    ibanOrAccount = { iban: ibanToUpperCase };
  } else {
    ibanOrAccount = { account: ibanToUpperCase };
  }

  return {
    individualId,
    companyId: '',
    file: file ?? '',
    paymentMethod: PaymentMethodEnum.BankTransfer,
    currencyCode,
    fileStorageId: '',
    bankName,
    bankCountryId,
    bic: swiftCode.toUpperCase(),
    ...ibanOrAccount,
  };
};

export const calculateTotalPaymentAmount = (payment: Payment[], fxRate: number): number => (
  payment.reduce((acc, cur) => {
    let sum = 0;
    if (isPendingTransaction(cur)) {
      sum += cur.currency_code === CurrencyEnum.EUR ? (cur.amount * fxRate) : cur.amount;
    }
    return sum + acc;
  }, 0)
);

export const getEurToUsdRate = (fxRates: FXRates[]): number => (
  fxRates.find(el => el.currencyTo === CurrencyEnum.EUR)?.rate || 1
);

// TODO: Add unit tests for epic and actions ART-4393
export const isActiveAccount = (account: OwnerTradingAccount): boolean => {
  if (isMyPortalTrade()) {
    return account.status === TradingAccountStatusesEnum.Active
    || account.status === TradingAccountStatusesEnum.ForApproval;
  }
  return account.status === TradingAccountStatusesEnum.Active;
};

// eslint-disable-next-line max-len
export const isActiveOrWithdrawalRestrictedAccount = (account: OwnerTradingAccount): boolean => isActiveAccount(account) || account.status === TradingAccountStatusesEnum.WithdrawalRestricted;

export const isAlaricFundsAccount = (
  account: OwnerTradingAccount,
): boolean => account.reference_id.startsWith(
  AccountPrefixByTradingPlatformEnum.AlaricFunds,
);

export const isPaymentBelongsToPlatformOrAdjustment = (
  payment: Payment,
): boolean => (PaymentProcessEnum.Adjustment === payment.payment_process
  && payment.tenant_id === config.auth.tenantId
) || isPaymentBelongsToPlatform(payment);

// Check if payment's account reference ID starts with HammerWeb prefix
export const isPaymentBelongsToPlatform = (
  payment: Payment,
): boolean => {
  const referenceId = payment.account_reference_id;

  if (isTennWeb() || isTennMob()) {
    return referenceId.startsWith(AccountPrefixByTradingPlatformEnum.TenN);
  }

  if (isHammerWeb()) {
    return referenceId.startsWith(AccountPrefixByTradingPlatformEnum.HammerWeb);
  }

  if (isMyPortalTrade()) {
    return (referenceId.startsWith(AccountPrefixByTradingPlatformEnum.AlaricFunds)
        && payment.payment_process === PaymentProcessEnum.Transfer)
      || referenceId.startsWith(AccountPrefixByTradingPlatformEnum.HammerWeb);
  }

  return false;
};

